import { MAX_DELAY_SECONDS, MAX_FREQUENCY_DAYS, AUDIENCE_VALUES, SURFACE_VALUES } from "./constants";

/** Blank means "no value", not an empty string. */
export const trimmedOrNull = (value) => (value || "").trim() || null;

export const toBoundedInt = (value, fallback, max) => {
  const parsed = Number.parseInt(value, 10);
  if (Number.isNaN(parsed)) return fallback;
  return Math.min(Math.max(parsed, 0), max);
};

/**
 * The request body, kept apart from the form component because it is the
 * contract with the backend rather than anything about the UI.
 *
 * Every optional text field goes out as null rather than "". An empty string
 * is a value: it would be stored, shipped to the storefront, and rendered as a
 * blank headline instead of as no headline at all.
 *
 * `position` is deliberately absent. The backend appends on create and leaves
 * the existing rank alone on edit, so sending it from a form that never shows
 * it could only reshuffle campaigns by accident.
 */
export const buildPayload = (form) => ({
  name: form.name.trim(),
  audience: form.audience,
  surface: form.surface,
  is_active: form.is_active,
  delay_seconds: toBoundedInt(form.delay_seconds, 5, MAX_DELAY_SECONDS),
  frequency_days: toBoundedInt(form.frequency_days, 7, MAX_FREQUENCY_DAYS),
  offer_id: form.offer_id ? Number(form.offer_id) : null,
  image: trimmedOrNull(form.image),
  ar_image: trimmedOrNull(form.ar_image),
  headline: trimmedOrNull(form.headline),
  ar_headline: trimmedOrNull(form.ar_headline),
  body: trimmedOrNull(form.body),
  ar_body: trimmedOrNull(form.ar_body),
  cta_label: trimmedOrNull(form.cta_label),
  ar_cta_label: trimmedOrNull(form.ar_cta_label),
  cta_url: trimmedOrNull(form.cta_url),
  ar_cta_url: trimmedOrNull(form.ar_cta_url),
  starts_at: form.starts_at || null,
  ends_at: form.ends_at || null,
});

/**
 * The same rules the backend enforces, checked here so an admin hears about
 * them while still looking at the form rather than after a round trip. The
 * backend remains the authority — this is a courtesy, not a gate.
 */
export const validate = (form) => {
  const errors = [];
  if (!form.name.trim()) errors.push("Campaign name is required");
  if (form.name.trim().length > 255) {
    errors.push("Campaign name must be 255 characters or fewer");
  }
  if (!AUDIENCE_VALUES.includes(form.audience)) errors.push("Audience is invalid");
  if (!SURFACE_VALUES.includes(form.surface)) errors.push("Surface is invalid");

  const hasSomethingToShow = [
    form.image, form.ar_image, form.headline, form.ar_headline,
  ].some((value) => (value || "").trim());
  if (!hasSomethingToShow) {
    errors.push("Add artwork or a headline — a popup with neither is an empty box");
  }

  const hasLabel = Boolean(trimmedOrNull(form.cta_label) || trimmedOrNull(form.ar_cta_label));
  const hasUrl = Boolean(trimmedOrNull(form.cta_url) || trimmedOrNull(form.ar_cta_url));
  if (hasLabel !== hasUrl) {
    errors.push("A button needs both a label and a link, or neither");
  }

  if (form.starts_at && form.ends_at && new Date(form.starts_at) >= new Date(form.ends_at)) {
    errors.push("End date must be after start date");
  }
  return errors;
};
